import {useRef} from 'react';

import {
    Alert,
    SimpleGrid,
} from '@mantine/core';
import {
    useNavigate,
    useParams,
} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useCards} from '../hooks/useCards';
import {useCardsFilter} from '../hooks/useCardsFilter';
import {useCardsSearch} from '../hooks/useCardsSearch';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {rbacActions} from '../../../config/rbac/actions';
import {Card} from '../types';
import {UseCardsSelectionResult} from '../hooks/useCardsSelection';

// FNP-918: migrated from a hand-rolled Mantine <Table> (TableHeaderRow +
// TableBody + RowSelectionDataCell + the app-local PaginationControls) to the
// shared MantineDataTable.
//
// The hand-rolled table was not broken — it rendered rows and supported
// selection — but it structurally could not have sorting, token pagination, or
// the loading/empty/error states, because all three come from the shared
// component. Five checklist gaps close by changing this one component (§3z).
//
// ⚠️ MONEY-PATH ADJACENT. Cards are payment instruments. This component was
// changed for LIST/TABLE compliance only: no tokenization, no Stripe Elements,
// and no create/update payload construction was touched. The edit drawer this
// table used to open inline is gone — editing goes through the customer-scoped
// route (`/customers/:id/cards/:cardId`), which already wraps the form in
// <Elements>. Opening a card form from a table row bypassed that wrapper.
//
// ⚠️ `src/components/PaginationControls` and `src/hooks/usePagination` are NOT
// deleted here. Other modules still import them; they come out in a later wave
// once every module is migrated.

interface RecordsTableProps {
    /**
     * Selection is read by IndexPage and passed down, because the delete
     * button lives in the page HEADER — a sibling, not a child of this table.
     * The state itself is the Redux cards.selection slice (FNP-951); taking it
     * as a prop keeps this component store-free and testable.
     */
    selection?: UseCardsSelectionResult;
}

export const RecordsTable = ({selection}: RecordsTableProps) => {

    const {allowedActions} = useVerifiedPermissions();
    const navigate = useNavigate();

    // Cards are customer-scoped: this table only ever renders inside
    // `/customers/:id/...`. The previous version THREW when the param was
    // absent, which turns a routing mistake into a blank error boundary. The
    // query is gated on the id instead, so a missing param renders the ordinary
    // empty state.
    const {id: customerId} = useParams();

    const {filters} = useCardsFilter();

    const {
        columns,
        // The loaded window BEFORE filtering — the denominator the footer's
        // "N of M loaded" claim is measured against.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
    } = useCards(customerId ?? '', filters);

    const {
        keyword,
        handleFilterByKeyword,
    } = useCardsSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // The select column is HIDDEN, not disabled, when the action is not
    // permitted (§2, §10). Gated on the same action the delete button is gated
    // on — a checkbox with no permitted batch action behind it is an affordance
    // that does nothing.
    const canSelect = !!selection
        && allowedActions.includes(rbacActions.DeleteCard);

    const selectedRecords = canSelect
        ? visibleItems.filter(item => selection.selection[item.id])
        : undefined;

    // TWO empty states that mean OPPOSITE things (§3). `noRecordsText` is a
    // single string and cannot express both: "no cards on file" for a search
    // that matched nothing sends an operator hunting for a bug.
    const noRecordsText = keyword.trim() !== ''
        ? I18n.get('No cards match this search')
        : I18n.get('No cards on file for this customer');

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            {/*
              * TableSearchForm, not SimpleTableBatchAction. The latter REQUIRES
              * `limit` + `handleItemsPerPageChange` and renders the rows-per-page
              * dropdown, which token pagination cannot honour — there is no page
              * to resize and no total to divide (§9). The previous table passed
              * `limit={10}` and a no-op handler to satisfy it, so the dropdown
              * rendered and did nothing.
              *
              * TableSearchForm is also the CLEARABLE one: its clear control
              * resets both the input and the module-scoped keyword store, so the
              * list cannot stay narrowed while the box looks empty.
              */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert
                    color='red'
                    title={I18n.get('Could not load cards')}
                >
                    {I18n.get(
                        'The billing API did not return the card list. Try Reload; '
                        + 'if it persists the API may be unavailable in this environment.',
                    )}
                </Alert>
            )}

            <MantineDataTable<Card>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far — the
                // same scope the keyword filter works on.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={canSelect
                    ? (records: Card[]) =>
                        selection.handleSelectAllRows(true, records)
                    : undefined}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                // Row click navigates to the detail page (§3). The customer id
                // is part of the path because a card is only addressable within
                // its customer.
                onRowClick={({record}: {record: Card}) => {
                    if (customerId) {
                        navigate(`/customers/${encodeURIComponent(customerId)}`
                            + `/cards/${encodeURIComponent(record.id)}`);
                    }
                }}
            />

            {/*
              * A SIBLING of the table, not a table prop: the table renders rows,
              * the footer answers "have I seen everything?" (§9).
              *
              * `loadedCount` is the rows in the browser — NEVER a dataset total.
              * `filterTerm` must be passed whenever a keyword is active, or a
              * filtered view silently reports the unfiltered state.
              */}
            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'card'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
